import { logger } from '../utils/logger';
import React, { useState, useEffect } from 'react';
import { getNotifications, completeNotification } from '../services/api';
import './NotificationSidebar.css';

interface Notification {
    key: string;
    type: 'danger' | 'warning' | 'info';
    category: 'minute' | 'document' | 'training' | 'drill' | 'employee' | 'annual';
    title: string;
    message: string;
    data: any;
}

const NotificationSidebar: React.FC = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [loading, setLoading] = useState(false);
    const [hasUnread, setHasUnread] = useState(false);

    const fetchNotifications = async () => {
        try {
            setLoading(true);
            const data = await getNotifications();
            const newNotifications = data || [];

            // If we have more notifications than before and sidebar is closed, mark as unread
            if (newNotifications.length > notifications.length && !isOpen) {
                setHasUnread(true);
            }

            setNotifications(newNotifications);
        } catch (error) {
            logger.error('Error fetching notifications:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchNotifications();
        // Refresh every 10 minutes
        const interval = setInterval(fetchNotifications, 10 * 60 * 1000);
        return () => clearInterval(interval);
    }, [isOpen, notifications.length]); // Added dependencies to accurately track state

    const getIcon = (category: string) => {
        switch (category) {
            case 'minute': return '🕒';
            case 'document': return '⚠️';
            case 'training': return '📅';
            case 'drill': return '🔥';
            case 'employee': return '👥';
            case 'annual': return '📊';
            default: return 'ℹ️';
        }
    };

    const toggleSidebar = () => {
        const nextState = !isOpen;
        setIsOpen(nextState);

        if (nextState) { // If opening
            setHasUnread(false);
            fetchNotifications();
        }
    };

    const handleComplete = async (notificationKey: string) => {
        try {
            await completeNotification(notificationKey);
            // Remove from local list
            setNotifications(prev => prev.filter(n => n.key !== notificationKey));
        } catch (error) {
            logger.error('Error completing notification:', error);
        }
    };

    return (
        <div className={`notification-sidebar-container ${!isOpen ? 'collapsed' : ''}`}>
            <div className="notification-sidebar">
                <div className="sidebar-header">
                    <h2>
                        <span style={{ marginRight: '8px' }}>🔔</span>
                        Bildirimler
                    </h2>
                    <span className="notification-count">{notifications.length}</span>
                </div>

                <div className="notification-list">
                    {loading && <div className="empty-notifications">Yükleniyor...</div>}

                    {!loading && notifications.length === 0 && (
                        <div className="empty-notifications">Yeni bildirim bulunmuyor.</div>
                    )}

                    {!loading && notifications.map((notification, index) => (
                        <div key={notification.key || index} className={`notification-item ${notification.type}`}>
                            <div className="notification-item-content">
                                <div className="notification-title">
                                    <span style={{ fontSize: '1.1rem' }}>{getIcon(notification.category)}</span>
                                    <span style={{ marginLeft: '8px' }}>{notification.title}</span>
                                </div>
                                <div className="notification-body">
                                    {notification.message}
                                </div>
                            </div>
                            <div className="notification-actions">
                                <button
                                    className="complete-btn"
                                    onClick={() => handleComplete(notification.key)}
                                    title="Tamamlandı olarak işaretle"
                                >
                                    ✓
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className={`sidebar-toggle ${hasUnread ? 'has-new' : ''} ${notifications.some(n => n.type === 'danger') ? 'danger-alert' : ''}`} onClick={toggleSidebar}>
                <span style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>
                    {isOpen ? '❮' : '❯'}
                </span>
            </div>
        </div>
    );
};

export default NotificationSidebar;
